import React, { useState, useEffect, useCallback } from "react";
import apiCall from "../../API/API";
import { toast } from "react-toastify";
import { Button, Container, Spinner } from "react-bootstrap";
import "./TokenSettings.css";

function toDatetimeLocalValue(iso) {
  if (iso == null || iso === "") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDisplayDate(iso) {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

const TokenSettings = () => {
  const [loading, setLoading] = useState(true);
  const [tokenLiveRate, setTokenLiveRate] = useState("");
  const [lockingPeriodLocal, setLockingPeriodLocal] = useState("");
  const [serverLockingIso, setServerLockingIso] = useState(null);
  const [serverTokenRate, setServerTokenRate] = useState(null);
  const [savingRate, setSavingRate] = useState(false);
  const [savingLocking, setSavingLocking] = useState(false);

  const fetchCompanyInfo = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiCall("GET", "/get-company-info");
    if (error) {
      toast.error(error.message || "Failed to load company info");
      setLoading(false);
      return;
    }
    const info = data?.company_info || {};
    const rate = info.token_live_rate;
    const lock = info.locking_period_end;

    setServerTokenRate(rate != null ? rate : null);
    setTokenLiveRate(rate != null && rate !== "" ? String(rate) : "");

    setServerLockingIso(lock ?? null);
    setLockingPeriodLocal(toDatetimeLocalValue(lock));

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCompanyInfo();
  }, [fetchCompanyInfo]);

  const handleUpdateTokenRate = async () => {
    const trimmed = String(tokenLiveRate).trim();
    let body;
    if (trimmed === "") {
      body = { token_live_rate: null };
    } else {
      const n = Number(trimmed);
      if (Number.isNaN(n)) {
        toast.error("Token live rate must be a valid number.");
        return;
      }
      body = { token_live_rate: n };
    }
    setSavingRate(true);
    const { data, error } = await apiCall("POST", "/update-company-info", body);
    setSavingRate(false);
    if (error) {
      toast.error(error.message || "Update failed");
      return;
    }
    toast.success(data?.message || "Token live rate updated.");
    fetchCompanyInfo();
  };

  const handleUpdateLockingPeriod = async () => {
    const trimmed = String(lockingPeriodLocal).trim();
    const body =
      trimmed === ""
        ? { locking_period_end: null }
        : { locking_period_end: new Date(trimmed).toISOString() };

    if (trimmed !== "" && Number.isNaN(new Date(trimmed).getTime())) {
      toast.error("Invalid date/time.");
      return;
    }

    setSavingLocking(true);
    const { data, error } = await apiCall("POST", "/update-company-info", body);
    setSavingLocking(false);
    if (error) {
      toast.error(error.message || "Update failed");
      return;
    }
    toast.success(data?.message || "Locking period updated.");
    fetchCompanyInfo();
  };

  if (loading) {
    return (
      <Container className="token-settings-page">
        <div className="token-settings-loading-panel">
          <Spinner animation="border" role="status" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="token-settings-page">
      <div className="bonanza-settings token-settings-bonanza">
        <h2 className="token-settings-page-title">Token Settings</h2>

        <div className="token-settings-grid">
          {/* <section className="token-settings-card" aria-labelledby="token-rate-heading">
            <h3 id="token-rate-heading" className="token-settings-card__heading">
              Token live rate
            </h3>
            <dl className="token-settings-meta">
              <dt>Current</dt>
              <dd>
                <strong>
                  {serverTokenRate != null && serverTokenRate !== ""
                    ? String(serverTokenRate)
                    : "Not set"}
                </strong>
              </dd>
            </dl>
            <div className="token-settings-field">
              <label htmlFor="token-live-rate-input">New value</label>
              <input
                id="token-live-rate-input"
                type="text"
                inputMode="decimal"
                placeholder="e.g. 0.05"
                value={tokenLiveRate}
                onChange={(e) => setTokenLiveRate(e.target.value)}
                autoComplete="off"
              />
            </div>
            <p className="token-settings-hint">
              Leave empty and update to clear the value on the server.
            </p>
            <div className="button-container">
              <Button
                variant="primary"
                onClick={handleUpdateTokenRate}
                disabled={savingRate}
              >
                {savingRate ? (
                  <>
                    <Spinner size="sm" className="me-2" animation="border" />
                    Updating…
                  </>
                ) : (
                  "Update token live rate"
                )}
              </Button>
            </div>
          </section> */}

          <section
            className="token-settings-card"
            aria-labelledby="locking-period-heading"
          >
            <h3
              id="locking-period-heading"
              className="token-settings-card__heading"
            >
              Swap period end time
            </h3>
            <dl className="token-settings-meta">
              <dt>Current</dt>
              <dd>
                <strong>{formatDisplayDate(serverLockingIso)}</strong>
              </dd>
            </dl>
            <div className="token-settings-field">
              <label htmlFor="locking-period-input">End (local)</label>
              <input
                id="locking-period-input"
                type="datetime-local"
                value={lockingPeriodLocal}
                onChange={(e) => setLockingPeriodLocal(e.target.value)}
              />
            </div>
            <p className="token-settings-hint">
              Clear the field and update to remove the end time on the server.
            </p>
            <div className="button-container">
              <Button
                variant="primary"
                onClick={handleUpdateLockingPeriod}
                disabled={savingLocking}
              >
                {savingLocking ? (
                  <>
                    <Spinner size="sm" className="me-2" animation="border" />
                    Updating…
                  </>
                ) : (
                  "Update locking period end"
                )}
              </Button>
            </div>
          </section>
        </div>
      </div>
    </Container>
  );
};

export default TokenSettings;
